/**
 * **Which days the booking grid shows, and how the trainer pages through them.**
 *
 * Two views, as the A1 canon draws them: a Monday-first WEEK, and a MONTH laid out in whole
 * Monday-first weeks from the week holding the 1st to the week holding the last day (4 to 6 rows —
 * FullCalendar's `fixedWeekCount: false`, which is why this computes the same span rather than
 * asking the grid). The span is the slice's own, because three reads are sized by it — the
 * schedule, the availability and the free starts — and asking them before the grid has mounted is
 * what lets a section wait for all of its reads.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import { dateTimeFormat } from '~/shared/lib/intlCache';

import {
  civilDate,
  isoWeekday,
  mondayOfDay,
  shiftDay,
  wallReading,
  type CivilDay,
} from './trainerClock';

export type CalendarView = 'week' | 'month';

export interface VisibleRange {
  readonly first: CivilDay;
  readonly last: CivilDay;
}

function firstOfMonth(day: CivilDay): CivilDay {
  return `${day.slice(0, 7)}-01`;
}

function lastOfMonth(day: CivilDay): CivilDay {
  const [year, month] = day.split('-').map(Number);
  const next = new Date(Date.UTC(year ?? 1970, month ?? 1, 1));
  return shiftDay(next.toISOString().slice(0, 10), -1);
}

/**
 * The day the grid is anchored on: the one the trainer paged to, or — before they paged — TODAY ON
 * THE TRAINER'S CLOCK. Never the browser's day: across a week or month boundary the two name
 * different ranges, and the trainer's own week is the one they came to see.
 */
export function shownAnchor(chosen: CivilDay | null, nowMs: number, zone: string): CivilDay {
  return chosen ?? wallReading(nowMs, zone).day;
}

/** The days the grid draws for `view` around `anchor`. */
export function visibleRange(view: CalendarView, anchor: CivilDay): VisibleRange {
  if (view === 'week') {
    const first = mondayOfDay(anchor);
    return { first, last: shiftDay(first, 6) };
  }
  const last = lastOfMonth(anchor);
  return { first: mondayOfDay(firstOfMonth(anchor)), last: shiftDay(last, 7 - isoWeekday(last)) };
}

/**
 * The anchor one page before (`-1`) or after (`+1`). A month pages to the 1st of the neighbouring
 * month, so paging from 31 January never skips February.
 */
export function stepAnchor(view: CalendarView, anchor: CivilDay, direction: -1 | 1): CivilDay {
  if (view === 'week') return shiftDay(anchor, 7 * direction);
  const [year, month] = anchor.split('-').map(Number);
  const target = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1 + direction, 1));
  return target.toISOString().slice(0, 10);
}

/**
 * The toolbar's title: `21–27 września 2026` for a week, `wrzesień 2026` for a month.
 *
 * `formatRange` collapses what the two ends share, which is exactly the canon's `21–27 września`
 * and also gives `28 września – 4 października 2026` across a month end without a table of cases.
 * Read in UTC on civil days at noon, so no zone can move a day.
 */
export function rangeTitle(view: CalendarView, anchor: CivilDay, locale: string): string {
  if (view === 'month') {
    return dateTimeFormat(locale, { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(
      civilDate(anchor),
    );
  }
  const { first, last } = visibleRange(view, anchor);
  return dateTimeFormat(locale, {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).formatRange(civilDate(first), civilDate(last));
}
